import { Link, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import SiteFooter from "./SiteFooter";
import BrandLogo from "./BrandLogo";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "./ui/sheet";
import { LayoutDashboard, User, Sparkles, KanbanSquare, Mail, Building2, Settings, LogOut, Users, Shield, Compass, Briefcase, MessageSquare, Menu } from "lucide-react";

// Sidebar stage-grouped nav (Feb 2026 redesign). Items are ordered to match
// the athlete's actual workflow — set up profile → discover brands → reach
// out → close deals. Stage headers render as small volt eyebrows.
function buildAthleteStages({ unread, isAgency, isAdmin }) {
  return [
    {
      key: "setup", label: "Set up",
      items: [
        { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard },
        { to: "/app/profile", label: "Profile", icon: User },
        { to: "/app/media-kit", label: "Media Kit", icon: Sparkles },
      ],
    },
    {
      key: "discover", label: "Discover brands",
      items: [
        { to: "/app/brand-directory", label: "Brand Directory", icon: Building2 },
        { to: "/app/positioning", label: "Positioning", icon: Compass },
      ],
    },
    {
      key: "engage", label: "Engage",
      items: [
        { to: "/app/outreach", label: "AI Outreach", icon: Mail },
        { to: "/app/crm", label: "CRM Pipeline", icon: KanbanSquare },
        { to: "/app/inbox", label: "Inbox", icon: MessageSquare, badge: unread },
      ],
    },
    {
      key: "close", label: "Close deals",
      items: [
        { to: "/app/deal-assistant", label: "Deal Assistant", icon: Briefcase },
      ],
    },
    {
      key: "account", label: "Account",
      items: [
        ...(isAgency ? [{ to: "/app/athletes", label: "Athletes", icon: Users }] : []),
        { to: "/app/settings", label: "Settings", icon: Settings },
        ...(isAdmin ? [{ to: "/app/admin", label: "Admin", icon: Shield }] : []),
      ],
    },
  ];
}

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [actingAs, setActingAs] = useState(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const onLogout = async () => {
    localStorage.removeItem("acting_as_id");
    localStorage.removeItem("acting_as_name");
    await logout();
    navigate("/");
  };

  useEffect(() => {
    const id = localStorage.getItem("acting_as_id");
    const name = localStorage.getItem("acting_as_name");
    if (id) setActingAs({ id, name });
  }, []);

  const stopActingAs = () => {
    localStorage.removeItem("acting_as_id");
    localStorage.removeItem("acting_as_name");
    setActingAs(null);
    // Force-reload so all open API requests get the agent's perspective again.
    window.location.href = "/app/dashboard";
  };

  useEffect(() => {
    let t;
    const tick = () => api.get("/conversations/inbox/unread-count")
      .then(({ data }) => setUnread(data.unread || 0)).catch(() => {});
    tick();
    t = setInterval(tick, 30000);
    return () => clearInterval(t);
  }, []);

  const isAgency = ["agent","club"].includes(user?.account_type);
  const STAGES = buildAthleteStages({ unread, isAgency, isAdmin: !!user?.is_admin });
  // Flat list for the mobile bottom tab bar (slice 0..4). We pull the 4 most-
  // common surfaces from across stages rather than just stage 1.
  const QUICK_TABS = [
    STAGES[0].items[0],                 // Dashboard
    STAGES[1].items[0],                 // Brand Directory
    STAGES[2].items[2],                 // Inbox
    STAGES[0].items[2],                 // Media Kit
  ];

  // Render helper for a single nav link — shared between desktop + mobile.
  const renderItem = (n, onClick) => (
    <NavLink
      key={n.to}
      to={n.to}
      onClick={onClick}
      data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g,"-")}`}
      className={({isActive}) =>
        `flex items-center justify-between gap-3 px-3 py-2.5 text-sm uppercase tracking-widest transition-colors ${
          isActive ? "bg-bg-elev text-volt border-l-2 border-volt" : "text-ink-muted hover:text-white"
        }`
      }
    >
      <span className="flex items-center gap-3">
        <n.icon className="w-4 h-4" />
        <span className="font-semibold">{n.label}</span>
      </span>
      {n.badge > 0 && (
        <span className="bg-volt text-black text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">{n.badge}</span>
      )}
    </NavLink>
  );

  return (
    <div className="min-h-screen flex bg-bg text-white">
      <aside className="hidden md:flex flex-col w-[260px] border-r border-line shrink-0 sticky top-0 h-screen">
        <Link to="/app/dashboard" className="px-6 py-5 border-b border-line flex items-center" data-testid="sidebar-logo">
          <BrandLogo variant="wordmark" size={28} showLink={false} />
        </Link>
        <nav className="flex-1 px-3 py-4 space-y-5 overflow-y-auto">
          {STAGES.map(stage => (
            <div key={stage.key} data-testid={`nav-stage-${stage.key}`}>
              <div className="px-3 pb-2 text-[10px] tracking-widest uppercase text-volt/80 font-bold">
                {stage.label}
              </div>
              <div className="space-y-1">
                {stage.items.map(n => renderItem(n))}
              </div>
            </div>
          ))}
        </nav>
        <div className="border-t border-line p-4">
          <div className="text-xs label-eyebrow mb-1">Signed in</div>
          <div className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user?.full_name || user?.email}</div>
          <div className="text-[10px] uppercase tracking-widest text-volt mt-1">{(user?.plan || "free")} plan</div>
          <button onClick={onLogout} data-testid="sidebar-logout-btn" className="mt-3 w-full btn-ghost-line flex items-center justify-center gap-2">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </aside>

      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-bg/90 backdrop-blur-xl border-b border-line px-4 py-3 flex justify-between items-center">
        <Link to="/app/dashboard" className="flex items-center">
          <BrandLogo variant="wordmark" size={20} showLink={false} />
        </Link>
        <div className="flex items-center gap-3">
          <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-nav-trigger"
                aria-label="Open menu"
                className="text-ink-muted hover:text-white p-1.5 -mr-1.5"
              >
                <Menu className="w-5 h-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-bg border-line text-white w-[78%] max-w-[320px] p-0 flex flex-col">
              <SheetTitle className="sr-only">Main navigation</SheetTitle>
              <div className="px-5 pt-6 pb-4 border-b border-line">
                <div className="text-xs label-eyebrow mb-1">Signed in</div>
                <div className="text-sm font-medium truncate" data-testid="mobile-nav-user-name">{user?.full_name || user?.email}</div>
                <div className="text-[10px] uppercase tracking-widest text-volt mt-1">{(user?.plan || "free")} plan</div>
              </div>
              <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-5">
                {STAGES.map(stage => (
                  <div key={stage.key} data-testid={`mobile-nav-stage-${stage.key}`}>
                    <div className="px-3 pb-2 text-[10px] tracking-widest uppercase text-volt/80 font-bold">
                      {stage.label}
                    </div>
                    <div className="space-y-1">
                      {stage.items.map(n => (
                        <NavLink
                          key={n.to}
                          to={n.to}
                          onClick={() => setMobileNavOpen(false)}
                          data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g,"-")}`}
                          className={({isActive}) =>
                            `flex items-center justify-between gap-3 px-3 py-3 text-sm uppercase tracking-widest transition-colors ${
                              isActive ? "bg-bg-elev text-volt border-l-2 border-volt" : "text-ink-muted hover:text-white"
                            }`
                          }
                        >
                          <span className="flex items-center gap-3">
                            <n.icon className="w-4 h-4" />
                            <span className="font-semibold">{n.label}</span>
                          </span>
                          {n.badge > 0 && (
                            <span className="bg-volt text-black text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">{n.badge}</span>
                          )}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                ))}
              </nav>
              <div className="border-t border-line p-4">
                <button onClick={onLogout} data-testid="mobile-nav-logout-btn" className="w-full btn-ghost-line flex items-center justify-center gap-2">
                  <LogOut className="w-4 h-4" /> Logout
                </button>
              </div>
            </SheetContent>
          </Sheet>
          <button onClick={onLogout} className="text-xs uppercase tracking-widest text-ink-muted" data-testid="mobile-logout-btn">Logout</button>
        </div>
      </div>

      <main className="flex-1 min-w-0 mt-14 md:mt-0">
        {actingAs && (
          <div className="bg-volt text-black px-4 py-2 flex items-center justify-between gap-3 text-xs uppercase tracking-widest sticky top-0 z-30" data-testid="acting-as-banner">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <span className="font-bold">Acting as</span>
              <span className="truncate">{actingAs.name || actingAs.id}</span>
              <span className="opacity-60 hidden md:inline">— all changes save to this athlete's workspace</span>
            </div>
            <button onClick={stopActingAs} className="bg-black text-volt px-3 py-1 hover:bg-bg-elev shrink-0" data-testid="stop-acting-as-btn">
              Exit
            </button>
          </div>
        )}
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-bg border-t border-line grid grid-cols-4">
          {QUICK_TABS.map((n) => (
            <NavLink key={n.to} to={n.to} className={({isActive}) => `flex flex-col items-center py-2 text-[10px] uppercase tracking-widest ${isActive ? "text-volt" : "text-ink-muted"}`}>
              <n.icon className="w-4 h-4 mb-1" /> {n.label.split(" ")[0]}
            </NavLink>
          ))}
        </div>
        <div className="pb-20 md:pb-0">{children}</div>
        <SiteFooter />
      </main>
    </div>
  );
}
